import React from "react";

import { css, cx } from "@emotion/css";
import type { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useStyles } from "@/hooks/useStyles.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { CardContent } from "@/uiKit/atoms/CardContent.tsx";
import { Form } from "@/uiKit/atoms/forms/Form";
import { Typography } from "@/uiKit/atoms/Typography.tsx";
import { PatientEmailFormField } from "@/uiKit/molecules/PatientCreationAndRenewal/PatientEmailFormField.tsx";
import { PatientFirstNameFormField } from "@/uiKit/molecules/PatientCreationAndRenewal/PatientFirstNameFormField.tsx";
import { PatientHelperPhoneFormField } from "@/uiKit/molecules/PatientCreationAndRenewal/PatientHelperPhoneFormField.tsx";
import { PatientLastNameFormField } from "@/uiKit/molecules/PatientCreationAndRenewal/PatientLastNameFormField.tsx";
import { PatientPhoneFormField } from "@/uiKit/molecules/PatientCreationAndRenewal/PatientPhoneFormField.tsx";
import { PatientInfo } from "@/uiKit/organisms/PatientCreationAndRenewal/PatientInfo/PatientInfo.ts";

export const PATIENT_INFO_FORM_ID = "patient-info-form";

export type PatientInfoFormContentProps = {
  formContext: UseFormReturn<PatientInfo>;
  onSubmit: (patientInfo: PatientInfo) => void;
  className?: string;
};

export const PatientInfoFormContent: React.FC<PatientInfoFormContentProps> = ({ formContext, onSubmit, className }) => {
  const styles = useStyles(makeStyles);
  const { t } = useTranslation();

  return (
    <CardContent className={cx(styles.root, className)} formExtraPadding>
      <Form id={PATIENT_INFO_FORM_ID} formContext={formContext} onSubmit={onSubmit} className={styles.form}>
        <Typography variant="subtitle" component="h6" className={styles.title}>
          {t("pages.patientMonitoring.patient.title")}
        </Typography>
        <div className={styles.sideBySide}>
          <PatientFirstNameFormField />
          <PatientLastNameFormField />
        </div>
        <div className={styles.sideBySide}>
          <PatientEmailFormField />
          <PatientPhoneFormField />
        </div>
        <Typography variant="subtitle" component="h6" className={styles.title}>
          {t("pages.patientMonitoring.patient.helperSection")}
        </Typography>
        <div>
          <PatientHelperPhoneFormField />
        </div>
      </Form>
    </CardContent>
  );
};

const makeStyles = (theme: Theme) => ({
  root: css({}),
  form: css({
    display: "flex",
    flexDirection: "column",
  }),
  sideBySide: css({
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(16),
    width: "100%",
  }),
  title: css({
    marginBottom: theme.spacing(12),
  }),
});
